// The Control Room's /v1/admin/* client. Money arrives as cents plus a
// display string and is shown as sent; the page never adds figures up.
import { call } from "../dashboard/api";
import { asTier, type Tier, type TierSplit } from "../tiers";

export type Money = { cents: number; display: string };
export type Queue = "ready" | "votes" | "review" | "won";

export type AdminTally = { username: string; email: string; votes: number; ratings: number };
export type AdminPerson = { username: string; email: string };
export type AdminCandidate = { user_id: string; username: string };

export type AdminContest = {
  id: string;
  slug: string;
  title: string;
  admin_created: boolean;
  published: boolean;
  open_submissions: boolean;
  genre: string;
  challenge: string;
  duration: string;
  cover_url: string;
  preview_url: string;
  admission: Money;
  cash_prize: Money;
  pot: Money;
  submissions: number;
  seats: number;
  contestants: number;
  reservations: number;
  views: number;
  winners_count: number;
  created_at: string;
  start_at: string;
  end_at: string;
  vote_emails_sent_at?: string;
  tallies: AdminTally[];
  voters: AdminPerson[];
  did_not_vote: string[];
  candidates: AdminCandidate[];
  winner?: AdminCandidate;
  won_at?: string;
};
export type ContestPage = { contests: AdminContest[]; total: number; page: number };

export type TierOption = TierSplit & { id: Tier };
export type Options = {
  genres: string[];
  challenges: string[];
  durations: string[];
  producers: string[];
  tiers: TierOption[];
};

// What the New contest page sends. There is no money in it: entry is free
// and the market pool is the prize.
export type NewContest = {
  name: string;
  description: string;
  genre: string;
  challenge: string;
  duration: string;
  seats: number;
  voting: "public" | "judges";
  publish: boolean;
  // Only sent when the creator picked one. The server stores Standard
  // for a body without it.
  tier?: Tier;
};
export type CreatedContest = { id: string; slug: string; title: string; published: boolean; url: string };
export type ContestUpload = { url: string; content_type: string; kind: string; headers: Record<string, string> };

// The Edit contest page's read. Every amount is cents plus the server's
// display string.
export type ManagedContest = {
  id: string;
  slug: string;
  title: string;
  description: string;
  genre: string;
  challenge: string;
  duration: string;
  seats: number;
  taken: number;
  voting: "public" | "judges";
  published: boolean;
  url: string;
  end_at: string;
  editable: boolean;
  locked_reason: string;
  entrants: AdminCandidate[];
  tier: Tier;
  // The tier's preset split, for pre-filling a new market. Null when the
  // server did not send one.
  tier_preset: TierSplit | null;
};
export type AdminMarketOutcome = {
  producer_id: string;
  username: string;
  staked: Money;
  house: Money;
  backers: number;
  odds: string;
};
export type AdminMarket = {
  id: string;
  status: "open" | "locked" | "settled" | "voided";
  tier: Tier;
  // True when the split has been moved off its tier's preset. The server
  // derives it on every read.
  is_custom: boolean;
  rake_bps: number;
  producer_bps: number;
  backing_bps: number;
  min_stake: Money;
  per_user_cap: Money;
  seed: Money;
  pool: Money;
  house: Money;
  stakes: number;
  void_reason: string;
  outcomes: AdminMarketOutcome[];
};
export type Managed = { contest: ManagedContest; market: AdminMarket | null };
export type ContestEdit = Omit<NewContest, "publish">;
export type MarketSettings = {
  rake_bps: number;
  producer_bps: number;
  backing_bps: number;
  min_stake_cents: number;
  per_user_cap_cents: number;
  seed_cents: number;
};

export type AdminUser = {
  id: string;
  username: string;
  email: string;
  paypal_email: string;
  instagram: string;
  joined_at: string;
  submissions: number;
  reservations: number;
  email_verified: boolean;
  is_active: boolean;
  is_admin: boolean;
  balance_display: string;
};
export type UserPage = { users: AdminUser[]; total: number; page: number };

export type WithdrawalState = "pending" | "completed" | "rejected";
export type AdminWithdrawal = {
  id: string;
  username: string;
  email: string;
  paypal_email: string;
  amount_display: string;
  requested_at: string;
  paid_at?: string;
  state: string;
  // What PayPal charged, present once it is on record, and the list-price
  // fee the fee input prefills.
  paypal_fee?: Money;
  default_paypal_fee?: Money;
};

export type PlatformMarket = { pool: Money; rake: Money; producer_cut: Money; dust: Money };
export type PlatformRow = {
  contest_id: string;
  slug: string;
  title: string;
  seeded: boolean;
  contestants: number;
  pot: Money;
  revenue: Money;
  cash_prize: Money;
  winner_paid: Money;
  market?: PlatformMarket;
};
export type PlatformTotals = {
  pot: Money;
  revenue: Money;
  cash_prize: Money;
  winner_paid: Money;
  pool: Money;
  rake: Money;
  producer_cut: Money;
  dust: Money;
  card_fees: Money;
  paypal_fees: Money;
  net: Money;
};
export type PlatformPeriod = {
  month: string;
  label: string;
  rake: Money;
  card_fees: Money;
  paypal_fees: Money;
  net: Money;
  estimated_paypal_fees: number;
};
export type Platform = {
  rows: PlatformRow[];
  periods: PlatformPeriod[];
  totals: PlatformTotals;
  markets_unavailable: boolean;
  fees_unavailable: boolean;
};

export type Usage = {
  queried_at: string;
  signups: {
    last7: number;
    last30: number;
    last90: number;
    ytd: number;
    total: number;
    monthly: { month: string; count: number }[];
    recent_signups: { email: string; username: string; createdAt: string; emailVerified: boolean; type: string }[];
  };
  editor: {
    site_configs: number;
    funnel: { user_id: string; email: string; username: string; loads: number; publishes: number; purchase_attempts: number }[];
  };
};

// GET /v1/admin/goals: what people told the earnings calculator they want
// to make. Money arrives as cents plus display.
export type GoalCount = { label: string; count: number };
export type GoalRole = {
  role: string;
  savers: number;
  median: Money;
  total: Money;
  buckets: GoalCount[];
  money: number;
  buzz: number;
};
export type GoalRow = { username: string; role: string; goal: Money; genre: string; focus: string; updated_at: string };
export type Goals = {
  savers: number;
  members: number;
  visitors: number;
  roles: GoalRole[];
  genres: GoalCount[];
  recent: GoalRow[];
};

export type QueueFilters = {
  genre: string;
  challenge: string;
  duration: string;
  producer: string;
  prizeMin: string;
  prizeMax: string;
  admissionMin: string;
  admissionMax: string;
  contestantsMin: string;
  contestantsMax: string;
  freeAdmission: boolean;
  versus: boolean;
};

export const EMPTY_FILTERS: QueueFilters = {
  genre: "",
  challenge: "",
  duration: "",
  producer: "",
  prizeMin: "",
  prizeMax: "",
  admissionMin: "",
  admissionMax: "",
  contestantsMin: "",
  contestantsMax: "",
  freeAdmission: false,
  versus: false,
};

// Dollar inputs become the whole cents the API filters on. The rail only
// narrows a search; nothing here is money that moves.
function dollarsToCents(v: string): string {
  const n = Number(v);
  return v.trim() === "" || !Number.isFinite(n) || n < 0 ? "" : String(Math.round(n * 100));
}

export function queueQuery(queue: Queue, f: QueueFilters, page: number, pageSize = 20): string {
  const p = new URLSearchParams({ queue, page: String(page), page_size: String(pageSize) });
  const set = (k: string, v: string) => {
    if (v.trim() !== "") p.set(k, v.trim());
  };
  set("genre", f.genre);
  set("challenge", f.challenge);
  set("duration", f.duration);
  set("producer", f.producer);
  set("prize_min", dollarsToCents(f.prizeMin));
  set("prize_max", dollarsToCents(f.prizeMax));
  set("admission_min", dollarsToCents(f.admissionMin));
  set("admission_max", dollarsToCents(f.admissionMax));
  set("contestants_min", f.contestantsMin);
  set("contestants_max", f.contestantsMax);
  if (f.freeAdmission) p.set("free_admission", "1");
  if (f.versus) p.set("versus", "1");
  return p.toString();
}

const obj = (raw: unknown) => (raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null);
const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const arr = (v: unknown) => (Array.isArray(v) ? v : []);
const money = (v: unknown): Money => {
  const m = obj(v);
  return { cents: num(m?.cents), display: str(m?.display) };
};
const httpsOnly = (v: unknown) => (/^https?:\/\//.test(str(v)) ? str(v) : "");

function normalizeContest(raw: unknown): AdminContest | null {
  const b = obj(raw);
  if (!b || typeof b.id !== "string") return null;
  const winner = obj(b.winner);
  return {
    id: b.id,
    slug: str(b.slug),
    title: str(b.title),
    admin_created: b.admin_created === true,
    published: b.published === true,
    open_submissions: b.open_submissions === true,
    genre: str(b.genre),
    challenge: str(b.challenge),
    duration: str(b.duration),
    cover_url: httpsOnly(b.cover_url),
    preview_url: httpsOnly(b.preview_url),
    admission: money(b.admission),
    cash_prize: money(b.cash_prize),
    pot: money(b.pot),
    submissions: num(b.submissions),
    seats: num(b.seats),
    contestants: num(b.contestants),
    reservations: num(b.reservations),
    views: num(b.views),
    winners_count: num(b.winners_count),
    created_at: str(b.created_at),
    start_at: str(b.start_at),
    end_at: str(b.end_at),
    vote_emails_sent_at: typeof b.vote_emails_sent_at === "string" ? b.vote_emails_sent_at : undefined,
    tallies: arr(b.tallies).map((t) => {
      const o = obj(t);
      return { username: str(o?.username), email: str(o?.email), votes: num(o?.votes), ratings: num(o?.ratings) };
    }),
    voters: arr(b.voters).map((v) => ({ username: str(obj(v)?.username), email: str(obj(v)?.email) })),
    did_not_vote: arr(b.did_not_vote).map(str),
    candidates: arr(b.candidates)
      .map((c) => ({ user_id: str(obj(c)?.user_id), username: str(obj(c)?.username) }))
      .filter((c) => c.user_id !== ""),
    winner: winner && typeof winner.user_id === "string" ? { user_id: winner.user_id, username: str(winner.username) } : undefined,
    won_at: typeof b.won_at === "string" ? b.won_at : undefined,
  };
}

function normalizeContestPage(raw: unknown): ContestPage | null {
  const b = obj(raw);
  if (!b || !Array.isArray(b.contests)) return null;
  return {
    contests: b.contests.map(normalizeContest).filter((c): c is AdminContest => c !== null),
    total: num(b.total),
    page: num(b.page),
  };
}

function normalizeOptions(raw: unknown): Options | null {
  const b = obj(raw);
  if (!b) return null;
  const list = (v: unknown) => arr(v).filter((x): x is string => typeof x === "string");
  const tiers = arr(b.tiers)
    .map((raw) => obj(raw))
    .filter((t): t is Record<string, unknown> => !!t && (t.id === "standard" || t.id === "home_turf"))
    .filter((t) => ["rake_bps", "producer_bps", "backing_bps"].every((k) => typeof t[k] === "number"))
    .map((t) => ({
      id: t.id as Tier,
      rake_bps: num(t.rake_bps),
      producer_bps: num(t.producer_bps),
      backing_bps: num(t.backing_bps),
    }));
  return {
    genres: list(b.genres),
    challenges: list(b.challenges),
    durations: list(b.durations),
    producers: list(b.producers),
    tiers,
  };
}

function normalizeUser(raw: unknown): AdminUser | null {
  const b = obj(raw);
  if (!b || typeof b.id !== "string") return null;
  return {
    id: b.id,
    username: str(b.username),
    email: str(b.email),
    paypal_email: str(b.paypal_email),
    instagram: str(b.instagram),
    joined_at: str(b.joined_at),
    submissions: num(b.submissions),
    reservations: num(b.reservations),
    email_verified: b.email_verified === true,
    is_active: b.is_active === true,
    is_admin: b.is_admin === true,
    balance_display: str(b.balance_display),
  };
}

function normalizeUserPage(raw: unknown): UserPage | null {
  const b = obj(raw);
  if (!b || !Array.isArray(b.users)) return null;
  return { users: b.users.map(normalizeUser).filter((u): u is AdminUser => u !== null), total: num(b.total), page: num(b.page) };
}

function normalizeWithdrawal(raw: unknown): AdminWithdrawal | null {
  const b = obj(raw);
  if (!b || typeof b.id !== "string") return null;
  return {
    id: b.id,
    username: str(b.username),
    email: str(b.email),
    paypal_email: str(b.paypal_email),
    amount_display: str(b.amount_display),
    requested_at: str(b.requested_at),
    paid_at: typeof b.paid_at === "string" ? b.paid_at : undefined,
    state: str(b.state),
    paypal_fee: obj(b.paypal_fee) ? money(b.paypal_fee) : undefined,
    default_paypal_fee: obj(b.default_paypal_fee) ? money(b.default_paypal_fee) : undefined,
  };
}

function normalizePlatform(raw: unknown): Platform | null {
  const b = obj(raw);
  const t = obj(b?.totals);
  if (!b || !t || !Array.isArray(b.rows)) return null;
  return {
    rows: b.rows
      .map((r): PlatformRow | null => {
        const o = obj(r);
        if (!o || typeof o.contest_id !== "string") return null;
        const m = obj(o.market);
        return {
          contest_id: o.contest_id,
          slug: str(o.slug),
          title: str(o.title),
          seeded: o.seeded === true,
          contestants: num(o.contestants),
          pot: money(o.pot),
          revenue: money(o.revenue),
          cash_prize: money(o.cash_prize),
          winner_paid: money(o.winner_paid),
          market: m
            ? { pool: money(m.pool), rake: money(m.rake), producer_cut: money(m.producer_cut), dust: money(m.dust) }
            : undefined,
        };
      })
      .filter((r): r is PlatformRow => r !== null),
    periods: arr(b.periods)
      .map((p): PlatformPeriod | null => {
        const o = obj(p);
        if (!o || typeof o.month !== "string") return null;
        return {
          month: o.month,
          label: str(o.label),
          rake: money(o.rake),
          card_fees: money(o.card_fees),
          paypal_fees: money(o.paypal_fees),
          net: money(o.net),
          estimated_paypal_fees: num(o.estimated_paypal_fees),
        };
      })
      .filter((p): p is PlatformPeriod => p !== null),
    totals: {
      pot: money(t.pot),
      revenue: money(t.revenue),
      cash_prize: money(t.cash_prize),
      winner_paid: money(t.winner_paid),
      pool: money(t.pool),
      rake: money(t.rake),
      producer_cut: money(t.producer_cut),
      dust: money(t.dust),
      card_fees: money(t.card_fees),
      paypal_fees: money(t.paypal_fees),
      net: money(t.net),
    },
    markets_unavailable: b.markets_unavailable === true,
    fees_unavailable: b.fees_unavailable === true,
  };
}

const goalCounts = (v: unknown): GoalCount[] =>
  arr(v).map((g) => ({ label: str(obj(g)?.label), count: num(obj(g)?.count) }));

function normalizeGoals(raw: unknown): Goals | null {
  const b = obj(raw);
  if (!b || !Array.isArray(b.roles)) return null;
  return {
    savers: num(b.savers),
    members: num(b.members),
    visitors: num(b.visitors),
    roles: arr(b.roles).map((r) => {
      const o = obj(r);
      return {
        role: str(o?.role),
        savers: num(o?.savers),
        median: money(o?.median),
        total: money(o?.total),
        buckets: goalCounts(o?.buckets),
        money: num(o?.money),
        buzz: num(o?.buzz),
      };
    }),
    genres: goalCounts(b.genres),
    recent: arr(b.recent).map((r) => {
      const o = obj(r);
      return {
        username: str(o?.username),
        role: str(o?.role),
        goal: money(o?.goal),
        genre: str(o?.genre),
        focus: str(o?.focus),
        updated_at: str(o?.updated_at),
      };
    }),
  };
}

function normalizeUsage(raw: unknown): Usage | null {
  const data = obj(obj(raw)?.data);
  const s = obj(data?.signups);
  if (!data || !s) return null;
  const e = obj(data.editor);
  return {
    queried_at: str(data.queried_at),
    signups: {
      last7: num(s.last7),
      last30: num(s.last30),
      last90: num(s.last90),
      ytd: num(s.ytd),
      total: num(s.total),
      monthly: arr(s.monthly).map((m) => ({ month: str(obj(m)?.month), count: num(obj(m)?.count) })),
      recent_signups: arr(s.recent_signups).map((r) => {
        const o = obj(r);
        return {
          email: str(o?.email),
          username: str(o?.username),
          createdAt: str(o?.createdAt),
          emailVerified: o?.emailVerified === true,
          type: str(o?.type),
        };
      }),
    },
    editor: {
      site_configs: num(e?.site_configs),
      funnel: arr(e?.funnel).map((f) => {
        const o = obj(f);
        return {
          user_id: str(o?.user_id),
          email: str(o?.email),
          username: str(o?.username),
          loads: num(o?.loads),
          publishes: num(o?.publishes),
          purchase_attempts: num(o?.purchase_attempts),
        };
      }),
    },
  };
}

const field = <T>(key: string, inner: (raw: unknown) => T | null) => (raw: unknown) => inner(obj(raw)?.[key]);
const withResult = (raw: unknown) => {
  const b = obj(raw);
  const w = normalizeWithdrawal(b?.withdrawal);
  return w && typeof b?.result === "string" ? { result: b.result, withdrawal: w } : null;
};
const winnerResult = (raw: unknown) => {
  const b = obj(raw);
  return b && typeof b.result === "string" ? { result: b.result, winner_username: str(b.winner_username) } : null;
};
const tallyResult = (raw: unknown) => {
  const b = obj(raw);
  if (!b || !Array.isArray(b.tallies)) return null;
  return {
    tallies: b.tallies.map((t) => ({ username: str(obj(t)?.username), votes: num(obj(t)?.votes), ratings: num(obj(t)?.ratings) })),
    voted: arr(b.voted).map(str),
    did_not_vote: arr(b.did_not_vote).map(str),
  };
};
const anyObject = (raw: unknown) => obj(raw);
const createdContest = (raw: unknown): CreatedContest | null => {
  const b = obj(obj(raw)?.contest);
  if (!b || typeof b.id !== "string" || b.id === "") return null;
  const url = str(b.url);
  return { id: b.id, slug: str(b.slug), title: str(b.title), published: b.published === true, url: url.startsWith("/") ? url : "" };
};
const MARKET_STATUSES: readonly AdminMarket["status"][] = ["open", "locked", "settled", "voided"];
// tierSplit keeps a preset only when the server sent all three cuts.
const tierSplit = (raw: unknown): TierSplit | null => {
  const b = obj(raw);
  if (!b || !["rake_bps", "producer_bps", "backing_bps"].every((k) => typeof b[k] === "number")) return null;
  return { rake_bps: num(b.rake_bps), producer_bps: num(b.producer_bps), backing_bps: num(b.backing_bps) };
};

const managed = (raw: unknown): Managed | null => {
  const b = obj(raw);
  const c = obj(b?.contest);
  if (!c || typeof c.id !== "string" || c.id === "") return null;
  const m = obj(b?.market);
  const url = str(c.url);
  return {
    contest: {
      id: c.id,
      slug: str(c.slug),
      title: str(c.title),
      description: str(c.description),
      genre: str(c.genre),
      challenge: str(c.challenge),
      duration: str(c.duration),
      seats: num(c.seats),
      taken: num(c.taken),
      voting: c.voting === "judges" ? "judges" : "public",
      published: c.published === true,
      url: url.startsWith("/") ? url : "",
      end_at: str(c.end_at),
      editable: c.editable === true,
      locked_reason: str(c.locked_reason),
      entrants: arr(c.entrants)
        .map((e) => ({ user_id: str(obj(e)?.user_id), username: str(obj(e)?.username) }))
        .filter((e) => e.user_id !== ""),
      tier: asTier(c.tier),
      tier_preset: tierSplit(c.tier_preset),
    },
    market:
      m && typeof m.id === "string" && MARKET_STATUSES.includes(m.status as AdminMarket["status"])
        ? {
            id: m.id,
            status: m.status as AdminMarket["status"],
            tier: asTier(m.tier),
            is_custom: m.is_custom === true,
            rake_bps: num(m.rake_bps),
            producer_bps: num(m.producer_bps),
            backing_bps: num(m.backing_bps),
            min_stake: money(m.min_stake),
            per_user_cap: money(m.per_user_cap),
            seed: money(m.seed),
            pool: money(m.pool),
            house: money(m.house),
            stakes: num(m.stakes),
            void_reason: str(m.void_reason),
            outcomes: arr(m.outcomes)
              .map((o) => {
                const x = obj(o);
                return {
                  producer_id: str(x?.producer_id),
                  username: str(x?.username),
                  staked: money(x?.staked),
                  house: money(x?.house),
                  backers: num(x?.backers),
                  odds: str(x?.odds),
                };
              })
              .filter((o) => o.producer_id !== ""),
          }
        : null,
  };
};
const headersOf = (v: unknown) => {
  const out: Record<string, string> = {};
  if (v && typeof v === "object") {
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) if (typeof val === "string") out[k] = val;
  }
  return out;
};
const contestUpload = (raw: unknown): ContestUpload | null => {
  const b = obj(raw);
  const url = httpsOnly(b?.url);
  return b && url ? { url, content_type: str(b.content_type), kind: str(b.kind), headers: headersOf(b.headers) } : null;
};
const withdrawalList = (raw: unknown) => {
  const b = obj(raw);
  return b && Array.isArray(b.withdrawals)
    ? b.withdrawals.map(normalizeWithdrawal).filter((w): w is AdminWithdrawal => w !== null)
    : null;
};

const FAILED = "That didn't go through. Nothing changed; try again.";
const id = encodeURIComponent;

export const adminApi = {
  queue: (queue: Queue, f: QueueFilters, page: number) =>
    call("GET", `/v1/admin/contests?${queueQuery(queue, f, page)}`, undefined, normalizeContestPage, FAILED),
  options: () => call("GET", "/v1/admin/contests/options", undefined, normalizeOptions, FAILED),
  createContest: (contest: NewContest) => call("POST", "/v1/admin/contests", contest, createdContest, FAILED),
  presignUpload: (contestID: string, filename: string) =>
    call("POST", `/v1/admin/contests/${id(contestID)}/upload`, { filename }, contestUpload, FAILED),
  managed: (contestID: string) => call("GET", `/v1/admin/contests/${id(contestID)}`, undefined, managed, FAILED),
  editContest: (contestID: string, edit: ContestEdit) =>
    call("PATCH", `/v1/admin/contests/${id(contestID)}`, edit, managed, FAILED),
  openMarket: (contestID: string, settings: MarketSettings) =>
    call("POST", `/v1/admin/contests/${id(contestID)}/market`, settings, managed, FAILED),
  tuneMarket: (contestID: string, settings: Partial<MarketSettings>) =>
    call("PATCH", `/v1/admin/contests/${id(contestID)}/market`, settings, managed, FAILED),
  backProducer: (contestID: string, producerID: string, amountCents: number, key: string) =>
    call(
      "POST",
      `/v1/admin/contests/${id(contestID)}/market/back`,
      { producer_id: producerID, amount_cents: amountCents, idempotency_key: key },
      managed,
      FAILED,
    ),
  selectWinner: (contestID: string, userID: string) =>
    call("POST", `/v1/admin/contests/${id(contestID)}/winner`, { user_id: userID }, winnerResult, FAILED),
  notifyVote: (contestID: string) =>
    call("POST", `/v1/admin/contests/${id(contestID)}/notify-vote`, {}, anyObject, FAILED),
  tallies: (contestID: string) => call("POST", `/v1/admin/contests/${id(contestID)}/tallies`, {}, tallyResult, FAILED),
  publish: (contestID: string, published: boolean) =>
    call("POST", `/v1/admin/contests/${id(contestID)}/${published ? "publish" : "unpublish"}`, {}, anyObject, FAILED),
  deleteContest: (contestID: string) => call("DELETE", `/v1/admin/contests/${id(contestID)}`, undefined, anyObject, FAILED),
  users: (q: string, page: number) =>
    call("GET", `/v1/admin/users?${new URLSearchParams({ q, page: String(page) })}`, undefined, normalizeUserPage, FAILED),
  deactivate: (userID: string) => call("DELETE", `/v1/admin/users/${id(userID)}`, undefined, field("user", normalizeUser), FAILED),
  reactivate: (userID: string) =>
    call("POST", `/v1/admin/users/${id(userID)}/reactivate`, {}, field("user", normalizeUser), FAILED),
  impersonate: (userID: string) => call("POST", `/v1/admin/users/${id(userID)}/impersonate`, {}, anyObject, FAILED),
  withdrawals: (state: WithdrawalState) =>
    call("GET", `/v1/admin/withdrawals?state=${state}`, undefined, withdrawalList, FAILED),
  // A fee of null leaves the body empty, and the server records the list
  // price for that amount.
  markPaid: (withdrawalID: string, paypalFeeCents: number | null) =>
    call(
      "POST",
      `/v1/admin/withdrawals/${id(withdrawalID)}/paid`,
      paypalFeeCents === null ? {} : { paypal_fee_cents: paypalFeeCents },
      withResult,
      FAILED,
    ),
  reject: (withdrawalID: string) => call("POST", `/v1/admin/withdrawals/${id(withdrawalID)}/reject`, {}, withResult, FAILED),
  platform: () => call("GET", "/v1/admin/platform", undefined, normalizePlatform, FAILED),
  usage: () => call("GET", "/v1/admin/analytics/usage", undefined, normalizeUsage, FAILED),
  goals: () => call("GET", "/v1/admin/goals", undefined, normalizeGoals, FAILED),
};

// endImpersonation is called from the nav on any page, as the impersonated
// user; the server decides whether the admin comes back.
export const endImpersonation = () => call("POST", "/v1/admin/impersonation/end", {}, anyObject, FAILED);

export function shortDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 2000) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
